import {tokens} from '@pleo-io/telescope'
import React, {FC} from 'react'
import styled, {css} from 'styled-components'

import PlayButton from '@/components/play-button'
import {checkmarkList} from '@/styles/lists'
import Image from '@helios/shared/components/image'
import {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import RichText, {Props as RichTextProps, hasRichText} from '@helios/shared/components/rich-text'
import {Video} from '@helios/shared/components/video'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {Layout} from '@helios/shared/styles/grid'
import {TextProps} from '@helios/shared/styles/typography'
import ImageSource from '@helios/shared/types/image'
import InlineVideo from '@helios/shared/types/inline-video'
import mq from '@helios/shared/utils/media-query'

import PopoverVideo from '../popover-video'
import CircularImageWithCta from './circular-image-with-cta'

export interface Props {
    image?: ImageSource
    preTitle?: string
    title?: RichTextProps['content']
    body?: RichTextProps['content']
    mirrored?: boolean
    titleSize?: TextProps['size']
    headingLevel?: 'h1' | 'h2' | 'h3'
    videoId?: string
    withQuotes?: RichTextProps['withQuotes']
    oversizedImage?: boolean
    bodyProps?: TextProps
    titleProps?: TextProps
    inlineVideo?: InlineVideo
    onPlayVideo?: () => void
    isCircularImage?: boolean
    ctaLink?: PrismicLinkProps
    ctaText?: string
}

const FeatureBlock: FC<Props> = ({
    children,
    image,
    ctaLink,
    ctaText,
    isCircularImage,
    preTitle,
    title,
    body,
    mirrored,
    titleSize = 'lg',
    headingLevel,
    videoId,
    withQuotes,
    oversizedImage,
    bodyProps,
    titleProps,
    inlineVideo,
    onPlayVideo,
}) => {
    const {play} = useGlobalStrings()

    const renderVisuals = () => {
        if (isCircularImage && image) {
            return (
                <CircularImageWithCta
                    image={image}
                    ctaLink={ctaLink}
                    ctaText={ctaText}
                    mirrored={mirrored}
                />
            )
        }
        if (inlineVideo && !videoId) {
            return (
                <Video
                    poster={image?.url}
                    video={inlineVideo}
                    posterWidth={oversizedImage ? 850 : 550}
                />
            )
        } else if (image) {
            return <Image image={image} width={oversizedImage ? 850 : 550} />
        } else {
            return null
        }
    }

    return (
        <Layout as={Wrapper} variant="two-asymmetrical" mirrored={mirrored} alignItems="center">
            <ContentWrapper>
                {!!preTitle && <RichText color="brand" content={preTitle} />}

                <PostTitleWrapper>
                    {(title || body) && (
                        <Body>
                            {hasRichText(title) && (
                                <RichText
                                    as={headingLevel}
                                    size={titleSize}
                                    content={title}
                                    withQuotes={withQuotes}
                                    {...titleProps}
                                />
                            )}

                            {hasRichText(body) && (
                                <div>
                                    <RichText color="muted" {...bodyProps} content={body} />
                                </div>
                            )}
                        </Body>
                    )}

                    {children}
                </PostTitleWrapper>
            </ContentWrapper>

            <ImageWrapper oversizedImage={oversizedImage} mirrored={mirrored}>
                {renderVisuals()}

                {videoId && (
                    <PlayButtonWrapper onClick={onPlayVideo}>
                        <PopoverVideo videoId={videoId}>
                            {({openPopover}) => (
                                <PlayButton onClick={openPopover} aria-label={play} />
                            )}
                        </PopoverVideo>
                    </PlayButtonWrapper>
                )}
            </ImageWrapper>
        </Layout>
    )
}

export default FeatureBlock

const PostTitleWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing32};

    ${mq.min('sm')} {
        justify-items: start;
    }
`

const Wrapper = styled.div`
    ${mq.between('sm', 'md')} {
        grid-template-columns: 1.4fr 1fr;
    }
`

type ImageWrapperProps = Pick<Props, 'oversizedImage' | 'mirrored'>

const ImageWrapper = styled.div<ImageWrapperProps>((props) => {
    const oversizedImage = css`
        width: 150%;
        max-width: none;
    `

    return css`
        position: relative;
        display: grid;
        justify-items: center;
        align-items: center;
        grid-row: 2;

        ${mq.min('sm')} {
            grid-row: initial;
        }

        img {
            justify-self: ${props.mirrored ? 'end' : 'start'};
            width: 100%;

            ${props.oversizedImage && oversizedImage}
        }
    `
})

const ContentWrapper = styled.div`
    display: grid;
    align-content: center;
    gap: ${tokens.spacing8};
    justify-items: stretch;

    ${mq.min('sm')} {
        justify-items: start;
    }
`

export const Body = styled.div`
    display: grid;
    gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        gap: ${tokens.spacing28};
    }

    ul {
        ${checkmarkList()}
    }
`

const PlayButtonWrapper = styled.div`
    position: absolute;
`
